import '../__generated__/styles/date-input.css';
import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import React, { useRef, useState, useLayoutEffect, useContext, useMemo, useCallback, useEffect } from 'react';
import clsx from 'clsx';
import { V2CalendarIcon, V2ChevronRightIcon, V2ChevronLeftIcon, V2ClockIcon } from '@swift-paypal/pp-react-icons';
import { MaskType, MaskedTextInput } from '@swift-paypal/pp-react-masked-text-input';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import { Sheet } from '@swift-paypal/pp-react-sheet';
import { IntlContext } from '@swift-paypal/pp-react-intl';
import { CaptionText, BodyText } from '@swift-paypal/pp-react-text';
import { IconButton } from '@swift-paypal/pp-react-icon-button';

var styles = {"ppvx_date-input":"ppvx_date-input___1-1-1","ppvx_date-input__calendar-trigger":"ppvx_date-input__calendar-trigger___1-1-1","ppvx_date-input__calendar-container":"ppvx_date-input__calendar-container___1-1-1","ppvx_calendar":"ppvx_calendar___1-1-1","ppvx_calendar__nav":"ppvx_calendar__nav___1-1-1","ppvx_calendar__prev--not-focusable":"ppvx_calendar__prev--not-focusable___1-1-1","ppvx_calendar__next--not-focusable":"ppvx_calendar__next--not-focusable___1-1-1","ppvx_calendar__prev--hidden":"ppvx_calendar__prev--hidden___1-1-1","ppvx_calendar__next--hidden":"ppvx_calendar__next--hidden___1-1-1","ppvx_calendar__table":"ppvx_calendar__table___1-1-1","ppvx_calendar__cell--in-range":"ppvx_calendar__cell--in-range___1-1-1","ppvx_calendar__cell--range-start":"ppvx_calendar__cell--range-start___1-1-1","ppvx_calendar__cell--range-end":"ppvx_calendar__cell--range-end___1-1-1","ppvx_calendar__date":"ppvx_calendar__date___1-1-1","ppvx_calendar__date--disabled":"ppvx_calendar__date--disabled___1-1-1","ppvx_calendar__date--not-current":"ppvx_calendar__date--not-current___1-1-1","ppvx_calendar__date--today":"ppvx_calendar__date--today___1-1-1","ppvx_calendar__date--selected":"ppvx_calendar__date--selected___1-1-1","ppvx_calendar__due-date":"ppvx_calendar__due-date___1-1-1","ppvx_calendar__due-date-icon-container":"ppvx_calendar__due-date-icon-container___1-1-1","ppvx_calendar__due-date-icon":"ppvx_calendar__due-date-icon___1-1-1"};

var MD_BREAKPOINT = 752;

var CalendarContainer = function (_ref) {
  var isCalendarVisible = _ref.isCalendarVisible,
      label = _ref.label,
      children = _ref.children,
      onShowCalendar = _ref.onShowCalendar;
  var containerRef = useRef();

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isMobile = _useState2[0],
      setMobile = _useState2[1];

  var checkMobile = function () {
    return window && window.innerWidth < MD_BREAKPOINT;
  };
  /* istanbul ignore next */


  var handleKey = function (evt) {
    if (evt.key === 'Escape') {
      onShowCalendar(false);
    }
  };
  /* istanbul ignore next */


  var handleOutsideClick = function (evt) {
    if (containerRef.current && !containerRef.current.contains(evt.target)) {
      onShowCalendar(false);
    }
  };

  useLayoutEffect(function () {
    var _isMobile = checkMobile();

    setMobile(_isMobile);

    if (!_isMobile && isCalendarVisible) {
      document.addEventListener('keydown', handleKey);
      document.addEventListener('click', handleOutsideClick);
    }

    return function () {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [isCalendarVisible]);

  if (isMobile) {
    /* istanbul ignore next */
    return /*#__PURE__*/React.createElement(Sheet, {
      isOpen: isCalendarVisible,
      onClose: function onClose() {
        return onShowCalendar(false);
      },
      theme: "v2",
      noCloseButton: true,
      title: label,
      skipFormFocus: true
    }, children);
  }

  return isCalendarVisible ? /*#__PURE__*/React.createElement("div", {
    className: styles['ppvx_date-input__calendar-container'],
    ref: containerRef
  }, children) : null;
};

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var DEFAULT_MASK_OPTIONS = {
  mask: 'm`/d`/y',
  lazy: false,
  blocks: {
    d: {
      mask: MaskType.Range,
      from: 1,
      to: 31,
      maxLength: 2,
      placeholderChar: 'D'
    },
    m: {
      mask: MaskType.Range,
      from: 1,
      to: 12,
      maxLength: 2,
      placeholderChar: 'M'
    },
    y: {
      mask: MaskType.Range,
      from: 1900,
      to: 2999,
      maxLength: 4,
      placeholderChar: 'Y'
    }
  }
};

function defaultParseDate(str) {
  /* istanbul ignore next */
  if (!str) {
    return null;
  }

  var parts = str.split('/');
  var month = Number(parts[0]);
  var date = Number(parts[1]);
  var year = Number(parts[2]);
  return new Date(year, month - 1, date);
}

function defaultFormatDate(date) {
  if (!date) {
    return '';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric'
  }).format(date);
}

function getDateMaskOptions(intlContext, timeZone, formatterSkeleton, maskOptions) {
  if (!(intlContext && intlContext.dateTimeFormatter && timeZone)) {
    return _objectSpread(_objectSpread({}, DEFAULT_MASK_OPTIONS), maskOptions);
  } // Get pattern from Worldready


  var dateTimeFormatter = intlContext.dateTimeFormatter({
    style: formatterSkeleton,
    timeZone: timeZone
  });
  var parts = dateTimeFormatter.formatToParts(new Date());
  var pattern = parts.map(function (_ref) {
    var type = _ref.type,
        value = _ref.value;

    switch (type) {
      case 'month':
        return 'm';

      case 'day':
        return 'd';

      case 'year':
        return 'y';

      default:
        return "`".concat(value);
    }
  }).join('');
  return _objectSpread(_objectSpread(_objectSpread({}, DEFAULT_MASK_OPTIONS), maskOptions), {}, {
    mask: pattern
  });
}

function getFormatters(intlContext, timeZone, formatterSkeleton, formatDate, parseDate) {
  if (!(intlContext && intlContext.dateTimeFormatter && timeZone)) {
    return {
      _formatDate: formatDate || defaultFormatDate,
      _parseDate: parseDate || defaultParseDate
    };
  } // Get formatters from Worldready


  var dateTimeFormatter = intlContext.dateTimeFormatter({
    style: formatterSkeleton,
    timeZone: timeZone
  });
  return {
    _formatDate: formatDate || function _formatDate(date) {
      /* istanbul ignore next */
      if (!date) {
        return '';
      }

      return dateTimeFormatter.format(date);
    },
    _parseDate: parseDate || function _parseDate(str) {
      return str && dateTimeFormatter.parse(str);
    }
  };
}

var DateInput = function (_ref2) {
  var name = _ref2.name,
      label = _ref2.label,
      className = _ref2.className,
      calendar = _ref2.calendar,
      isCalendarVisible = _ref2.isCalendarVisible,
      onShowCalendar = _ref2.onShowCalendar,
      maskOptions = _ref2.maskOptions,
      selectedDate = _ref2.selectedDate,
      onSelection = _ref2.onSelection,
      parseDate = _ref2.parseDate,
      formatDate = _ref2.formatDate,
      timeZone = _ref2.timeZone,
      _ref2$formatterSkelet = _ref2.formatterSkeleton,
      formatterSkeleton = _ref2$formatterSkelet === void 0 ? 'yyyyMMdd' : _ref2$formatterSkelet,
      sheetContainerRef = _ref2.sheetContainerRef,
      onComplete = _ref2.onComplete,
      onAccept = _ref2.onAccept,
      rest = _objectWithoutProperties(_ref2, ["name", "label", "className", "calendar", "isCalendarVisible", "onShowCalendar", "maskOptions", "selectedDate", "onSelection", "parseDate", "formatDate", "timeZone", "formatterSkeleton", "sheetContainerRef", "onComplete", "onAccept"]);

  var intlContext = useContext(IntlContext);

  var _maskOptions = useMemo(function () {
    return getDateMaskOptions(intlContext, timeZone, formatterSkeleton, maskOptions);
  }, [timeZone, formatterSkeleton, maskOptions]);

  var _useMemo = useMemo(function () {
    return getFormatters(intlContext, timeZone, formatterSkeleton, formatDate, parseDate);
  }, [timeZone, formatDate, parseDate]),
      _formatDate = _useMemo._formatDate,
      _parseDate = _useMemo._parseDate;

  var icon = calendar && /*#__PURE__*/React.createElement("div", {
    className: styles['ppvx_date-input__calendar-trigger'],
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement(V2CalendarIcon, {
    size: "sm",
    onClick: function handleIconClick() {
      onShowCalendar(true);
    }
  }), /*#__PURE__*/React.createElement(CalendarContainer, {
    isCalendarVisible: isCalendarVisible,
    onShowCalendar: onShowCalendar,
    label: label,
    sheetContainerRef: sheetContainerRef
  }, calendar));

  var unmaskedValue = _formatDate(selectedDate).replace(/\D/g, '');

  return /*#__PURE__*/React.createElement(MaskedTextInput, _extends({
    name: name,
    label: label,
    rightIcon: icon,
    theme: "v2",
    maskOptions: _maskOptions,
    className: clsx(styles['ppvx_date-input'], className),
    inputMode: "numeric"
  }, rest, {
    onComplete: function handleComplete(value, unmaskedValue, typedValue) {
      onSelection(_parseDate(value));

      if (onComplete) {
        onComplete(value, unmaskedValue, typedValue);
      }
    },
    onAccept: function handleAccept(value, unmaskedValue, typedValue) {
      if (!unmaskedValue) {
        onSelection(null);
      }

      if (onAccept) {
        onAccept(value, unmaskedValue, typedValue);
      }
    },
    value: unmaskedValue
  }));
};

var MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
var DAY_NAMES = {
  sun: 'Sun',
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat'
};

function getFormattedDateFromParts(parts) {
  return parts.reduce(function (acc, _ref) {
    var type = _ref.type,
        value = _ref.value;

    if (type === 'month' || type === 'day' || type === 'year') {
      acc[type] = value;
    }

    return acc;
  }, {});
}

function defaultFormatToParts(date) {
  var parts = new Intl.DateTimeFormat('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric'
  }).formatToParts(date);
  return getFormattedDateFromParts(parts);
}

function isDateInRange(date, selectedDate, range, rangeCompletionDate) {
  if (range === 'start' && selectedDate && rangeCompletionDate) {
    return date.getTime() >= selectedDate.getTime() && date.getTime() <= rangeCompletionDate.getTime();
  } else if (range === 'end' && selectedDate && rangeCompletionDate) {
    return date.getTime() <= selectedDate.getTime() && date.getTime() >= rangeCompletionDate.getTime();
  }

  return false;
}

function isDateEqual(date, providedDate) {
  return providedDate && date.getTime() === providedDate.getTime();
}

function isDateRangeStart(date, selectedDate, range, rangeCompletionDate) {
  return range === 'start' && selectedDate && date.getTime() === selectedDate.getTime() || range === 'end' && rangeCompletionDate && date.getTime() === rangeCompletionDate.getTime();
}

function isDateRangeEnd(date, selectedDate, range, rangeCompletionDate) {
  return range === 'end' && selectedDate && date.getTime() === selectedDate.getTime() || range === 'start' && rangeCompletionDate && date.getTime() === rangeCompletionDate.getTime();
}

function getDatesForMonth(date, daysOfWeek, firstDayOfWeek, selectedDate, range, rangeCompletionDate, dueDate, isDateDisabled, calendarRange) {
  var dates = [];
  var startMonth = date.getMonth();
  var startYear = date.getFullYear();
  var from = new Date(startYear, startMonth, 1);
  var firstday = Object.keys(DAY_NAMES)[from.getDay()];
  var firstDayIndex = daysOfWeek.indexOf(firstday);
  var firstDOWIndex = Object.keys(DAY_NAMES).indexOf(firstDayOfWeek);
  var todayDate = new Date();
  var today = new Date(todayDate.getFullYear(), todayDate.getMonth(), todayDate.getDate());
  from.setDate(-firstDayIndex + 1);

  while (from) {
    var month = from.getMonth();
    var day = from.getDate();
    var year = from.getFullYear();

    if (year === startYear && month > startMonth || year > startYear) {
      if (from.getDay() === firstDOWIndex) break;
    }

    var _dateObj = new Date(year, month, day);

    var isDisabled = isDateDisabled && isDateDisabled(_dateObj);

    if (calendarRange === 'start' && (month < startMonth || year < startYear)) {
      isDisabled = true;
    }

    if (calendarRange === 'end' && (month > startMonth || year > startYear)) {
      isDisabled = true;
    }

    dates.push({
      day: day,
      date: _dateObj,
      isDisabled: isDisabled,
      isToday: isDateEqual(from, today),
      inCurrentMonth: month === startMonth,
      isSelected: isDateEqual(from, selectedDate),
      isDue: isDateEqual(from, dueDate),
      inRange: isDateInRange(from, selectedDate, range, rangeCompletionDate),
      isRangeStart: isDateRangeStart(from, selectedDate, range, rangeCompletionDate),
      isRangeEnd: isDateRangeEnd(from, selectedDate, range, rangeCompletionDate)
    });
    from.setDate(day + 1);
  }

  return dates;
}

function getDaysOfWeek(firstDayOfWeek) {
  var daysOfWeek = Object.keys(DAY_NAMES);
  var firstDOWIndex = daysOfWeek.indexOf(firstDayOfWeek);

  if (firstDOWIndex === 0) {
    return daysOfWeek;
  }

  return daysOfWeek.slice(firstDOWIndex).concat(daysOfWeek.slice(0, firstDOWIndex));
}

function getFirstDayOfWeek(intlContext, firstDayOfWeek) {
  if (intlContext && intlContext.worldReadyObj && intlContext.worldReadyObj.weekInfo) {
    return firstDayOfWeek || intlContext.worldReadyObj.weekInfo.firstDay;
  }

  return firstDayOfWeek || 'sun';
}

function getNames(intlContext, monthNames, dayNames) {
  if (intlContext && intlContext.getDisplayNamesObject) {
    var monthDisplayNames = intlContext.getDisplayNamesObject({
      type: 'month',
      style: 'wide'
    });

    var _monthNames = monthDisplayNames.select(['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12']);

    var dayDisplayNames = intlContext.getDisplayNamesObject({
      type: 'day',
      style: 'abbreviated'
    });
    var keys = Object.keys(DAY_NAMES);
    var values = dayDisplayNames.select(keys);

    var _dayNames = keys.reduce(function (acc, key, index) {
      acc[key] = values[index];
      return acc;
    }, {});

    return {
      _monthNames: monthNames || _monthNames,
      _dayNames: dayNames || _dayNames
    };
  }

  return {
    _monthNames: monthNames || MONTH_NAMES,
    _dayNames: dayNames || DAY_NAMES
  };
}

function getFormatter(intlContext, formatToParts, timeZone, formatterSkeleton) {
  if (intlContext && intlContext.dateTimeFormatter && timeZone) {
    var dateTimeFormatter = intlContext.dateTimeFormatter({
      style: formatterSkeleton,
      timeZone: timeZone
    });
    return formatToParts || function fn(date) {
      var parts = dateTimeFormatter.formatToParts(date);
      return getFormattedDateFromParts(parts);
    };
  }

  return formatToParts || defaultFormatToParts;
}

function areMonthsEqual(date1, date2) {
  return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth();
}
/* eslint-disable complexity */


var Calendar = function (_ref2) {
  var className = _ref2.className,
      monthNames = _ref2.monthNames,
      dayNames = _ref2.dayNames,
      firstDayOfWeek = _ref2.firstDayOfWeek,
      _ref2$prevMonthLabel = _ref2.prevMonthLabel,
      prevMonthLabel = _ref2$prevMonthLabel === void 0 ? 'Go to prev month' : _ref2$prevMonthLabel,
      _ref2$nextMonthLabel = _ref2.nextMonthLabel,
      nextMonthLabel = _ref2$nextMonthLabel === void 0 ? 'Go to next month' : _ref2$nextMonthLabel,
      selectedDate = _ref2.selectedDate,
      range = _ref2.range,
      rangeCompletionDate = _ref2.rangeCompletionDate,
      isFocusable = _ref2.isFocusable,
      dateAs = _ref2.dateAs,
      calendarFrom = _ref2.calendarFrom,
      calendarTo = _ref2.calendarTo,
      navClassName = _ref2.navClassName,
      tableClassName = _ref2.tableClassName,
      dateClassName = _ref2.dateClassName,
      dueDate = _ref2.dueDate,
      formatToParts = _ref2.formatToParts,
      timeZone = _ref2.timeZone,
      _ref2$formatterSkelet = _ref2.formatterSkeleton,
      formatterSkeleton = _ref2$formatterSkelet === void 0 ? 'yyyyMd' : _ref2$formatterSkelet,
      isDateDisabled = _ref2.isDateDisabled,
      onSelection = _ref2.onSelection,
      onMonthChange = _ref2.onMonthChange;

  var _currentDate = selectedDate || new Date();

  var _useState = useState(new Date(_currentDate.getFullYear(), _currentDate.getMonth())),
      _useState2 = _slicedToArray(_useState, 2),
      currentDate = _useState2[0],
      setCurrentDate = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isRTL = _useState4[0],
      setRTL = _useState4[1];

  var intlContext = useContext(IntlContext);
  var containerRef = useCallback(function (node) {
    if (node !== null) {
      var computedStyle = window.getComputedStyle(node);
      setRTL(computedStyle && computedStyle.direction === 'rtl');
    }
  }, []);

  var _firstDayOfWeek = useMemo(function () {
    return getFirstDayOfWeek(intlContext, firstDayOfWeek);
  }, [firstDayOfWeek]);

  var _useMemo = useMemo(function () {
    return getNames(intlContext, monthNames, dayNames);
  }, [monthNames, dayNames]),
      _monthNames = _useMemo._monthNames,
      _dayNames = _useMemo._dayNames;

  var _formatToParts = useMemo(function () {
    return getFormatter(intlContext, formatToParts, timeZone, formatterSkeleton);
  }, [formatToParts, timeZone, formatterSkeleton]);

  var tabIndex = !isFocusable ? -1 : 0;
  useEffect(function () {
    setCurrentDate(new Date(_currentDate.getFullYear(), _currentDate.getMonth()));
  }, [selectedDate]);

  var renderDateButton = function (dateInfo) {
    var isDisabled = dateInfo.isDisabled,
        isToday = dateInfo.isToday,
        isSelected = dateInfo.isSelected,
        isRangeStart = dateInfo.isRangeStart,
        isRangeEnd = dateInfo.isRangeEnd,
        isDue = dateInfo.isDue,
        inCurrentMonth = dateInfo.inCurrentMonth,
        date = dateInfo.date;
    var cls = clsx(styles.ppvx_calendar__date, isDisabled && styles['ppvx_calendar__date--disabled'], isToday && styles['ppvx_calendar__date--today'], !inCurrentMonth && styles['ppvx_calendar__date--not-current'], (isSelected || isRangeStart || isRangeEnd) && styles['ppvx_calendar__date--selected'], dateClassName);
    var props = {
      className: cls,
      onClick: function onClick() {
        return !(isDisabled || isSelected || isRangeStart || isRangeEnd) && onSelection(date);
      },
      disabled: isDisabled,
      tabIndex: tabIndex
    };

    if (dateAs) {
      props.dateInfo = dateInfo;
    }

    var formattedDate = _formatToParts(date);

    var button = /*#__PURE__*/React.createElement(dateAs || 'button', props, /*#__PURE__*/React.createElement(BodyText, {
      as: "span",
      theme: "v2"
    }, formattedDate.day));

    if (isDue) {
      return /*#__PURE__*/React.createElement("div", {
        className: styles['ppvx_calendar__due-date']
      }, button, /*#__PURE__*/React.createElement("div", {
        className: styles['ppvx_calendar__due-date-icon-container']
      }, /*#__PURE__*/React.createElement(V2ClockIcon, {
        className: styles['ppvx_calendar__due-date-icon']
      })));
    }

    return button;
  };

  var currentMonth = _monthNames[currentDate.getMonth()];

  var daysOfWeek = getDaysOfWeek(_firstDayOfWeek);
  var header = /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, daysOfWeek.map(function (name) {
    return /*#__PURE__*/React.createElement("th", {
      key: name
    }, /*#__PURE__*/React.createElement(CaptionText, {
      theme: "v2"
    }, _dayNames[name]));
  })));
  var isPrevHidden = calendarFrom && areMonthsEqual(currentDate, calendarFrom);
  var isNextHidden = calendarTo && areMonthsEqual(currentDate, calendarTo);
  var calendarRange = isPrevHidden ? 'start' : isNextHidden ? 'end' : '';
  var dates = getDatesForMonth(currentDate, daysOfWeek, _firstDayOfWeek, selectedDate, range, rangeCompletionDate, dueDate, isDateDisabled, calendarRange);
  var dateRows = [];

  for (var i = 0; i < dates.length; i = i + 7) {
    var dateCols = [];

    for (var j = 0; j < 7; j++) {
      var _dates = dates[i + j],
          isRangeStart = _dates.isRangeStart,
          isRangeEnd = _dates.isRangeEnd,
          inRange = _dates.inRange;
      var cls = clsx(inRange && styles['ppvx_calendar__cell--in-range'], inRange && isRangeStart && styles['ppvx_calendar__cell--range-start'], inRange && isRangeEnd && styles['ppvx_calendar__cell--range-end']);
      dateCols.push( /*#__PURE__*/React.createElement("td", {
        key: j
      }, /*#__PURE__*/React.createElement("div", {
        className: cls
      }, renderDateButton(dates[i + j]))));
    }

    dateRows.push( /*#__PURE__*/React.createElement("tr", {
      key: i
    }, dateCols));
  }

  var body = /*#__PURE__*/React.createElement("tbody", null, dateRows);

  var formattedCurrentDate = _formatToParts(currentDate);
  /* istanbul ignore next */


  return /*#__PURE__*/React.createElement("div", {
    className: clsx(styles.ppvx_calendar, className),
    ref: containerRef
  }, /*#__PURE__*/React.createElement("div", {
    className: clsx(styles.ppvx_calendar__nav, navClassName)
  }, /*#__PURE__*/React.createElement(IconButton, {
    theme: "v2",
    svgIconComponent: isRTL ? V2ChevronRightIcon : V2ChevronLeftIcon,
    buttonType: "tertiary",
    onClick: function handlePrevClick() {
      currentDate.setMonth(currentDate.getMonth() - 1);
      var prevDate = new Date(currentDate.getFullYear(), currentDate.getMonth());
      setCurrentDate(prevDate);
      /* istanbul ignore next */

      if (onMonthChange) {
        onMonthChange(prevDate);
      }
    },
    className: clsx(styles.ppvx_calendar__prev, !isFocusable && styles['ppvx_calendar__prev--not-focusable'], isPrevHidden && styles['ppvx_calendar__prev--hidden']),
    tabIndex: tabIndex
  }, prevMonthLabel), /*#__PURE__*/React.createElement(BodyText, {
    theme: "v2"
  }, "".concat(currentMonth, " ").concat(formattedCurrentDate.year)), /*#__PURE__*/React.createElement(IconButton, {
    theme: "v2",
    svgIconComponent: isRTL ? V2ChevronLeftIcon : V2ChevronRightIcon,
    buttonType: "tertiary",
    onClick: function handleNextClick() {
      currentDate.setMonth(currentDate.getMonth() + 1);
      var nextDate = new Date(currentDate.getFullYear(), currentDate.getMonth());
      setCurrentDate(nextDate);
      /* istanbul ignore next */

      if (onMonthChange) {
        onMonthChange(nextDate);
      }
    },
    className: clsx(styles.ppvx_calendar__next, !isFocusable && styles['ppvx_calendar__next--not-focusable'], isNextHidden && styles['ppvx_calendar__next--hidden']),
    tabIndex: tabIndex
  }, nextMonthLabel)), /*#__PURE__*/React.createElement("table", {
    className: clsx(styles.ppvx_calendar__table, tableClassName)
  }, header, body));
};

export { Calendar, DateInput };
